import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, Shield, Eye, Lock, Database, UserCheck, Mail } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header de navigation */}
      <Header />
      
      {/* Header de la page */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-16">
        <div className="container mx-auto px-4">
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-white/80 hover:text-white transition-colors duration-200 mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour à l'accueil</span>
          </Link>
          
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold font-serif">Politique de confidentialité</h1>
              <p className="text-xl text-white/80 mt-2">Protection de vos données personnelles</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          
          {/* Introduction */}
          <section className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <Eye className="w-6 h-6 text-green-600" />
              <h2 className="text-2xl font-bold text-gray-800">Notre engagement</h2>
            </div>
            
            <div className="space-y-4 text-gray-600 text-justify">
              <p className="text-lg leading-relaxed">
                La communauté <strong>Fleurs de la Sainte Famille (FSF)</strong> s'engage à protéger 
                la confidentialité et la sécurité de vos données personnelles conformément aux valeurs 
                chrétiennes de respect et de dignité de la personne humaine.
              </p>
              
              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                <p className="text-green-800">
                  <strong>Principe fondamental :</strong> Nous collectons uniquement les données 
                  nécessaires au fonctionnement de notre communauté et à l'amélioration de nos services spirituels.
                </p>
              </div>
            </div>
          </section>

          {/* Données collectées */}
          <section className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <Database className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-800">Données que nous collectons</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800 text-lg text-left">Données d'inscription</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start space-x-2">
                    <UserCheck className="w-4 h-4 mt-1 text-blue-500" />
                    <span>Nom et prénom</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Mail className="w-4 h-4 mt-1 text-blue-500" />
                    <span>Adresse email</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <UserCheck className="w-4 h-4 mt-1 text-blue-500" />
                    <span>Informations sur votre couple (si applicable)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <UserCheck className="w-4 h-4 mt-1 text-blue-500" />
                    <span>Zone géographique</span>
                  </li>
                </ul>
              </div>
              
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800 text-lg text-left">Données d'utilisation</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start space-x-2">
                    <Database className="w-4 h-4 mt-1 text-green-500" />
                    <span>Historique de navigation sur le site</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Database className="w-4 h-4 mt-1 text-green-500" />
                    <span>Préférences de contenu spirituel</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Database className="w-4 h-4 mt-1 text-green-500" />
                    <span>Contributions et témoignages</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Database className="w-4 h-4 mt-1 text-green-500" />
                    <span>Participation aux événements</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Utilisation des données */}
          <section className="bg-white rounded-2xl shadow-lg p-8 mb-8 text-justify">
            <div className="flex items-center space-x-3 mb-6">
              <Lock className="w-6 h-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-gray-800">Comment nous utilisons vos données</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-amber-700 mb-3">Finalités spirituelles</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="bg-amber-50 p-3 rounded-lg">
                    <strong>Accompagnement spirituel :</strong> Proposer des contenus adaptés à votre cheminement de foi
                  </li>
                  <li className="bg-amber-50 p-3 rounded-lg">
                    <strong>Vie communautaire :</strong> Faciliter les échanges entre membres de la communauté FSF
                  </li>
                  <li className="bg-amber-50 p-3 rounded-lg">
                    <strong>Événements :</strong> Vous informer des activités et célébrations
                  </li>
                </ul>
              </div>
              
              <div className="space-y-4 text-justify">
                <h3 className="font-semibold text-blue-800 mb-3">Finalités techniques</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="bg-blue-50 p-3 rounded-lg">
                    <strong>Sécurité :</strong> Protéger votre compte et nos services
                  </li>
                  <li className="bg-blue-50 p-3 rounded-lg">
                    <strong>Amélioration :</strong> Optimiser l'expérience utilisateur du site
                  </li>
                  <li className="bg-blue-50 p-3 rounded-lg">
                    <strong>Communication :</strong> Newsletter et informations importantes
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Partage des données */}
          <section className="bg-white rounded-2xl shadow-lg p-8 mb-8 text-justify">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Partage et protection</h2>
            
            <div className="space-y-6">
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <h3 className="font-semibold text-red-800 mb-2">Principe de non-commercialisation</h3>
                <p className="text-red-700">
                  Nous ne vendons, ne louons, ni ne partageons jamais vos données à des fins commerciales. 
                  Nos valeurs chrétiennes nous interdisent toute exploitation marchande des informations personnelles.
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <Shield className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-green-800">Chiffrement</h4>
                  <p className="text-sm text-green-700 mt-1">Toutes les données sont chiffrées</p>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <Lock className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-blue-800">Accès restreint</h4>
                  <p className="text-sm text-blue-700 mt-1">Seuls les responsables autorisés</p>
                </div>
                
                <div className="bg-amber-50 p-4 rounded-lg text-center">
                  <Database className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <h4 className="font-semibold text-amber-700">Sauvegarde</h4>
                  <p className="text-sm text-amber-600 mt-1">Backups sécurisés réguliers</p>
                </div>
              </div>
            </div>
          </section>

          {/* Droits des utilisateurs */}
          <section className="bg-white rounded-2xl shadow-lg p-8 mb-8 text-justify">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Vos droits</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800">Droits d'accès et de contrôle</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start space-x-2">
                    <Eye className="w-4 h-4 mt-1 text-blue-500" />
                    <span>Consulter vos données personnelles</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <UserCheck className="w-4 h-4 mt-1 text-green-500" />
                    <span>Modifier vos informations</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Database className="w-4 h-4 mt-1 text-amber-500" />
                    <span>Demander la suppression de votre compte</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Mail className="w-4 h-4 mt-1 text-orange-500" />
                    <span>Vous désabonner de la newsletter</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-justify">
                <h4 className="font-semibold text-yellow-800 mb-2">Comment exercer vos droits</h4>
                <p className="text-sm text-yellow-700 mb-3">
                  Contactez-nous à tout moment pour exercer vos droits :
                </p>
                <div className="text-sm text-yellow-800">
                  <p><strong>Email :</strong> dpo@fleursdelasaintefamille.org</p>
                  <p><strong>Délai de réponse :</strong> Maximum 30 jours</p>
                </div>
              </div>
            </div>
          </section>

          {/* Cookies */}
          <section className="bg-white rounded-2xl shadow-lg p-8 mb-8 text-justify">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Cookies et technologies similaires</h2>
            
            <div className="space-y-4 text-gray-600">
              <p>
                Notre site utilise des cookies uniquement pour améliorer votre expérience de navigation 
                et mémoriser vos préférences spirituelles.
              </p>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2">Cookies essentiels</h4>
                  <p className="text-sm text-green-700">Nécessaires au fonctionnement du site</p>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-2">Cookies de préférence</h4>
                  <p className="text-sm text-blue-700">Mémorisent vos paramètres spirituels</p>
                </div>
                
                <div className="bg-amber-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-amber-700 mb-2">Cookies analytiques</h4>
                  <p className="text-sm text-amber-600">Améliorent l'expérience (anonymes)</p>
                </div>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4">Contact pour la protection des données</h2>
            
            <div className="space-y-4 text-justify">
              <p>
                Notre équipe de protection des données est à votre disposition pour toute question 
                ou préoccupation concernant vos données personnelles.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-2">Responsable de la protection des données</h3>
                  <p><strong>Email :</strong> dpo@fleursdelasaintefamille.org</p>
                  <p><strong>Téléphone :</strong> +229 XX XX XX XX</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Délégué à la protection des données</h3>
                  <p><strong>Adresse :</strong> FSF - Service Protection des Données</p>
                  <p className="text-sm text-white/80 mt-2">
                    Réponse garantie sous 30 jours ouvrables
                  </p>
                </div>
              </div>
              
              <p className="text-sm text-white/80 mt-6">
                Dernière mise à jour de cette politique : {new Date().toLocaleDateString('fr-FR')}
              </p>
            </div>
          </section>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;